import { useMemo } from 'react';
import { View, type ViewProps } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { Icon } from '~/global/components/icon/Icon';
import type { IconProps } from '~/global/components/icon/Icon';
import { format } from 'date-fns';
import { ConsumedDateRange } from '@/lib/local-api/meal/consumed-date-range';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { useQuery } from '@apollo/client/react';
import { type Meal, MealsByIdsDocument } from '~/graphql/generated/graphql';
import { MealUtils } from '@fytness/shared';

interface StatCardProps {
  icon: IconProps['name'];
  value: string;
  label: string;
}

const StatCard = ({ icon, value, label }: StatCardProps) => (
  <View className="flex-1 rounded-xl bg-surface p-4 gap-2">
    <View className="h-9 w-9 rounded-lg bg-surface-elevated items-center justify-center">
      <Icon name={icon} size={18} className="text-accent" />
    </View>
    <Text
      className="font-sora-bold text-2xl text-text-primary"
      style={{ letterSpacing: -1 }}
    >
      {value}
    </Text>
    <Text className="font-inter-medium text-xs text-text-muted">{label}</Text>
  </View>
);

export interface MealStatsSectionProps extends ViewProps {}

export const MealStatsSection = ({
  className,
  ...props
}: MealStatsSectionProps) => {
  const { t } = useTranslation('meal');

  const now = new Date();
  const weekStart = ConsumedDateRange.startOfWeekFor(now);
  // The SQL bounds are a deliberate superset (widened a day each side); the
  // exact local-day decision is made in memory by `filterToDays`.
  const { fromDate, toDate } = ConsumedDateRange.forWeekToDate(now);

  const { data: rawMealLogs = [] } = useLivePowerSyncQuery<{
    id: string;
    mealId: string;
    consumedAmount: number | null;
    consumedAt: string;
  }>(db.MealLog.queries.findByDateRange(fromDate, toDate), [fromDate, toDate]);

  // `fromDate`/`toDate` are the memo keys: they change only when the week window
  // does, unlike the per-render `now`/`weekStart` Date objects.
  const mealLogs = useMemo(
    () => ConsumedDateRange.filterToDays(rawMealLogs, weekStart, now),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawMealLogs, fromDate, toDate],
  );

  const mealIds = useMemo(
    () => Array.from(new Set(mealLogs.map((l) => l.mealId).filter(Boolean))),
    [mealLogs],
  );

  const { data: mealsData } = useQuery(MealsByIdsDocument, {
    variables: { ids: mealIds as string[] },
    skip: mealIds.length === 0,
  });

  const mealsById = useMemo(() => {
    const map = new Map<string, Meal>();
    (mealsData?.mealsByIds ?? []).forEach((m) => map.set(m.id, m as Meal));
    return map;
  }, [mealsData]);

  const averages = useMemo(() => {
    if (mealLogs.length === 0) {
      return { calories: 0, protein: 0, carbs: 0, fats: 0 };
    }

    const dailyTotals = new Map<
      string,
      { calories: number; protein: number; carbs: number; fats: number }
    >();

    for (const log of mealLogs) {
      const meal = mealsById.get(log.mealId);
      if (!meal) continue;
      const adj = MealUtils.adjustedNutrition(meal, log.consumedAmount ?? 0);
      const dayKey = format(
        ConsumedDateRange.toCalendarDate(log.consumedAt),
        'yyyy-MM-dd',
      );

      const existing = dailyTotals.get(dayKey) ?? {
        calories: 0,
        protein: 0,
        carbs: 0,
        fats: 0,
      };
      existing.calories += adj.calories ?? 0;
      existing.protein += adj.protein ?? 0;
      existing.carbs += adj.carbs ?? 0;
      existing.fats += adj.fats ?? 0;
      dailyTotals.set(dayKey, existing);
    }

    const dayCount = dailyTotals.size || 1;
    let totalCalories = 0;
    let totalProtein = 0;
    let totalCarbs = 0;
    let totalFats = 0;

    for (const day of dailyTotals.values()) {
      totalCalories += day.calories;
      totalProtein += day.protein;
      totalCarbs += day.carbs;
      totalFats += day.fats;
    }

    return {
      calories: Math.round(totalCalories / dayCount),
      protein: Math.round(totalProtein / dayCount),
      carbs: Math.round(totalCarbs / dayCount),
      fats: Math.round(totalFats / dayCount),
    };
  }, [mealLogs, mealsById]);

  return (
    <View className={className} {...props}>
      <View className="flex flex-row items-center mb-3 gap-3">
        <View className="h-0.5 w-6 rounded-sm bg-accent" />
        <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
          {t('stats.title')}
        </Text>
      </View>

      <View className="flex flex-col gap-3">
        <View className="flex flex-row gap-3">
          <StatCard
            icon="Flame"
            value={averages.calories.toLocaleString()}
            label={t('stats.avgCalories')}
          />
          <StatCard
            icon="Beef"
            value={`${averages.protein}g`}
            label={t('stats.avgProtein')}
          />
        </View>
        <View className="flex flex-row gap-3">
          <StatCard
            icon="Wheat"
            value={`${averages.carbs}g`}
            label={t('stats.avgCarbs')}
          />
          <StatCard
            icon="Droplets"
            value={`${averages.fats}g`}
            label={t('stats.avgFats')}
          />
        </View>
      </View>
    </View>
  );
};
